import React from 'react';
import { Button, Grid, Icon } from 'semantic';
import ThemedSegment from 'components/semantic/Segment';
import { TFunction, useTranslation } from 'react-i18next';
import {
  TariffComponent as TariffComponentType,
  DynamicPrice,
  BasePrice,
  TimePrice,
} from 'subdomains/cpo/screens/GroupsAndPolicies/types';
import {
  formatDateRestriction,
  formatDaysOfWeekRestriction,
  formatDurationRestriction,
  formatEnergyRestriction,
  formatTimeOfDayRestriction,
} from 'subdomains/cpo/screens/GroupsAndPolicies/Tariff/Preview';

type Props = {
  values: TariffComponentType;
  currency: string;
  onEdit?: () => void;
  onRemove?: () => void;
  dragHandleProps?: any;
};

export const TariffComponentPreview: React.FC<Props> = ({
  values,
  currency,
  onEdit,
  onRemove,
  dragHandleProps,
}) => {
  const { t } = useTranslation();

  const displayContent = getCombinedPriceAndRestrictionsString(
    values,
    currency,
    t
  );

  return (
    <ThemedSegment offbackground>
      <Grid columns="equal" verticalAlign="middle">
        <Grid.Row>
          <Grid.Column style={{ width: '1rem', flexGrow: 0 }}>
            <div {...dragHandleProps}>
              <Icon size="medium" name="arrows-up-down-left-right" />
            </div>
          </Grid.Column>
          <Grid.Column width={8}>
            <p>{displayContent}</p>
          </Grid.Column>
          <Grid.Column floated="right" width={2}>
            {onEdit && (
              <Button icon="edit" basic size="mini" onClick={onEdit} />
            )}
            {onRemove && (
              <Button
                icon="trash"
                basic
                size="mini"
                onClick={onRemove} // TODO: add prompt?
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </ThemedSegment>
  );
};

export function getCombinedPriceAndRestrictionsString(
  values: TariffComponentType,
  currency: string,
  t: TFunction<'translation', undefined>
): React.ReactNode {
  const parts: React.ReactNode[] = [];

  parts.push(<span>{formatPriceType(values.price, t)}</span>);
  parts.push(': ');
  const restrictionsString = formatRestrictions(values.restrictions, t);
  if (restrictionsString) {
    parts.push(<span>{restrictionsString}</span>);
    parts.push(' - ');
  }
  parts.push(<strong>{formatPriceValue(values.price, currency, t)}</strong>);

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>{part}</React.Fragment>
      ))}
    </>
  );
}

function formatPriceType(
  price: TariffComponentType['price'],
  t: TFunction<'translation', undefined>
): string {
  if (!price) return '';

  switch (price?.type) {
    case 'DYNAMIC':
      return t('tariff.component.priceTypeDynamic', 'Dynamic price');
    case 'ENERGY':
      return t('tariff.component.priceTypeEnergy', 'Energy price');
    case 'TIME':
      return t('tariff.component.priceTypeTime', 'Time price');
    case 'PARKING_TIME':
      return t('tariff.component.priceTypeParkingTime', 'Parking time price');
    case 'FLAT':
      return t('tariff.component.priceTypeFlat', 'Flat price');
    default:
      return t('tariff.component.priceTypeUnknown', 'Unknown price type');
  }
}

function formatPriceValue(
  price: TariffComponentType['price'],
  currency: string,
  t: TFunction<'translation', undefined>
): string {
  if (!price) return '';

  const currencySymbol = '€'; // TODO: get currency symbol properly

  switch (price?.type) {
    case 'DYNAMIC':
      return t(
        'tariff.component.previewDynamicPriceValue',
        'Surcharge: {{currencySymbol}}{{surcharge}}/kWh, Backup: {{currencySymbol}}{{default}}/kWh',
        {
          currencySymbol,
          surcharge: (price as DynamicPrice).surcharge,
          default: (price as DynamicPrice).backup,
        }
      );
    case 'ENERGY':
    case 'FLAT':
      return t(
        'tariff.component.previewBasePriceValue',
        '{{currencySymbol}}{{price}}/{{unit}}',
        {
          currencySymbol,
          price: (price as BasePrice).value,
          unit: price.type === 'ENERGY' ? 'kWh' : 'session',
        }
      );
    case 'TIME':
    case 'PARKING_TIME':
      return t(
        'tariff.component.previewTimePriceValue',
        '{{currencySymbol}}{{price}}/{{format}}',
        {
          currencySymbol,
          price: (price as TimePrice).value,
          format: (price as TimePrice).unit,
        }
      );
    default:
      return '';
  }
}

function formatRestrictions(
  restrictions: TariffComponentType['restrictions'],
  t: TFunction<'translation', undefined>
): string {
  if (!restrictions || !restrictions.enabled) return '';

  const restrictionParts: string[] = [];

  const timeOfDayString = formatTimeOfDayRestriction(restrictions.timeOfDay, t);
  if (timeOfDayString) restrictionParts.push(timeOfDayString);

  const durationString = formatDurationRestriction(restrictions.duration, t);
  if (durationString) restrictionParts.push(durationString);

  const daysOfWeekString = formatDaysOfWeekRestriction(restrictions.daysOfWeek);
  if (daysOfWeekString) restrictionParts.push(daysOfWeekString);

  const dateString = formatDateRestriction(restrictions.date);
  if (dateString) restrictionParts.push(dateString);

  const energyString = formatEnergyRestriction(restrictions.energy, t);
  if (energyString) restrictionParts.push(energyString);

  return restrictionParts.join(', ');
}
